import React from "react";
import { CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDashboardData } from "@/services/dashboardDataService";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import "./widgets.css";
import { cleanDateAndTimeObject } from "@shared/renderDate";

interface SupplierCountData {
  totalSuppliers: number;
  lastUpdated?: {
    toDate?: () => Date;
  };
  error?: boolean;
  errorMessage?: string;
}

const TotalSupplierCountWidget: React.FC = () => {
  const { data, isLoading, error, refetch } = useDashboardData('supplier-count');
  const supplierData = data as SupplierCountData | undefined;

  // Report widget status to dashboard context
  useWidgetStatusReporter('supplier-count', isLoading, error, !!data);
  
  // Handle loading state
  if (isLoading || !data) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
        <h3 className="text-lg font-semibold mb-2">Total Supplier Count</h3>
        <div className="flex flex-col items-center justify-center flex-1 w-full h-full" style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}>
          <Skeleton className="h-16 w-16" style={{
            fontSize: 'clamp(2.5rem, 8cqw, 7rem)',
          }} />
          <Skeleton className="h-4 w-24" />
        </div>
      </CardContent>
    );
  }

  // Handle error state
  if (error || supplierData?.error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">Total Supplier Count</h3>
        <Alert variant="destructive" className="w-full max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2">
            <span>{error || supplierData?.errorMessage || 'Failed to load supplier count'}</span>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 self-start"
              onClick={() => refetch()}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <div className="flex justify-between items-center w-full mb-2">
        <h3 className="text-lg font-semibold">Total Supplier Count</h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => refetch()}
          title="Refresh data"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex flex-col items-center justify-center flex-1 w-full h-full" style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}>
        <span
          className="font-bold text-primary"
          style={{
            fontSize: 'clamp(2.5rem, 8cqw, 7rem)',
            lineHeight: 1,
            minHeight: '1em',
          }}
        >
          {supplierData?.totalSuppliers.toLocaleString() ?? '0'}
        </span>
        <span
          className="text-muted-foreground"
          style={{
            fontSize: 'clamp(1rem, 2.5cqw, 2rem)',
            marginTop: 'clamp(0.25rem, 1cqw, 1rem)',
          }}
        >
          total supplier{supplierData?.totalSuppliers !== 1 ? 's' : ''}
        </span>
      </div>
      
      {supplierData?.lastUpdated && (
        <div className="mt-2 text-xs text-muted-foreground text-center">
          Updated: {cleanDateAndTimeObject(supplierData.lastUpdated).toLocaleTimeString()}
        </div>
      )}
    </CardContent>
  );
};

export default TotalSupplierCountWidget;
